import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle } from "react";
import { useTranslation } from "react-i18next";
import { Loader2, SearchIcon, User, X } from "lucide-react";
import SmallLoadingSpinner from "../assets/SmallLoadingSpinner";
import axios from "axios";
import { HRBasicData } from "../types/Types";

interface EmployeeInputSelectProps {
  value?: string;
  onChange: (value: string) => void;
  placeholder?: string;
  required?: boolean;
  label?: string | boolean;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  enableRef?: boolean;
  disabled?: boolean;
  inTable?: boolean; // When true, removes borders for excel-like table appearance
}

const EmployeeInputSelect = forwardRef<HTMLInputElement, EmployeeInputSelectProps>(
  ({ value, onChange, placeholder, required = false, onKeyDown, enableRef = false, label, disabled = false, inTable = false }, ref) => {
    const { t } = useTranslation();

    const [searchTerm, setSearchTerm] = useState("");
    const [isOpen, setIsOpen] = useState(false);
    const [highlighted, setHighlighted] = useState(-1);
    const [filteredEmployees, setFilteredEmployees] = useState<HRBasicData[]>([]);
    const [loading, setLoading] = useState(false);
    const [selectedEmployee, setSelectedEmployee] = useState<HRBasicData | null>(null);

    const inputRef = useRef<HTMLInputElement>(null);
    const dropdownRef = useRef<HTMLUListElement>(null);
    const ignoreBlur = useRef(false);
    const isTypingRef = useRef(false);
    const cacheRef = useRef<Map<string, HRBasicData>>(new Map());

    useImperativeHandle(ref, () => inputRef.current as HTMLInputElement, [enableRef]);

    // Load employee by ID when value changes
    useEffect(() => {
      if (isTypingRef.current) return;

      if (!value) {
        setSearchTerm("");
        setSelectedEmployee(null);
        return;
      }

      // Check cache first
      if (cacheRef.current.has(value)) {
        const employee = cacheRef.current.get(value)!;
        setSelectedEmployee(employee);
        setSearchTerm(`${employee.firstName} ${employee.lastName}`);
        return;
      }

      // Load from API
      const loadEmployee = async () => {
        try {
          const apiUrl = process.env.REACT_APP_API_URL ?? '';
          const token = localStorage.getItem('token');
          if (!token) return;

          const response = await axios.get<HRBasicData>(`${apiUrl}/api/hr-basic-data/${value}`, {
            headers: { 'x-auth-token': token },
          });

          const employee = response.data;
          cacheRef.current.set(value, employee);
          setSelectedEmployee(employee);
          setSearchTerm(`${employee.firstName} ${employee.lastName}`);
        } catch (error) {
          console.error('Error loading employee:', error);
          setSearchTerm("");
          setSelectedEmployee(null);
        }
      };

      loadEmployee();
    }, [value]);

    // Reset typing flag when dropdown closes
    useEffect(() => {
      if (!isOpen) {
        isTypingRef.current = false;
      }
    }, [isOpen]);

    // Search employees when typing
    useEffect(() => {
      if (!isOpen || !isTypingRef.current) return;

      const searchEmployees = async () => {
        setLoading(true);
        try {
          const apiUrl = process.env.REACT_APP_API_URL ?? '';
          const token = localStorage.getItem('token');
          if (!token) return;

          const response = await axios.get(`${apiUrl}/api/hr-basic-data`, {
            headers: { 'x-auth-token': token },
            params: {
              search: searchTerm,
              sort: 'firstName',
              order: 'asc',
              page: 1,
              limit: 50,
            },
          });

          const employees = response.data.basicData || [];

          // Cache all returned employees
          employees.forEach((emp: HRBasicData) => {
            cacheRef.current.set(emp._id, emp);
          });

          setFilteredEmployees(employees);
        } catch (error) {
          console.error('Error searching employees:', error);
          setFilteredEmployees([]);
        } finally {
          setLoading(false);
        }
      };

      const handler = setTimeout(searchEmployees, 300);
      return () => clearTimeout(handler);
    }, [searchTerm, isOpen]);

    // Load initial employees when dropdown opens
    useEffect(() => {
      if (isOpen && !isTypingRef.current) {
        const loadInitialEmployees = async () => {
          setLoading(true);
          try {
            const apiUrl = process.env.REACT_APP_API_URL ?? '';
            const token = localStorage.getItem('token');
            if (!token) return;

            const response = await axios.get(`${apiUrl}/api/hr-basic-data`, {
              headers: { 'x-auth-token': token },
              params: {
                search: '',
                sort: 'firstName',
                order: 'asc',
                page: 1,
                limit: 50,
              },
            });

            const employees = response.data.basicData || [];

            // Cache all returned employees
            employees.forEach((emp: HRBasicData) => {
              cacheRef.current.set(emp._id, emp);
            });

            setFilteredEmployees(employees);
          } catch (error) {
            console.error('Error loading employees:', error);
            setFilteredEmployees([]);
          } finally {
            setLoading(false);
          }
        };

        loadInitialEmployees();
      }
    }, [isOpen]);

    // Close dropdown on outside click
    useEffect(() => {
      const handleClickOutside = (e: MouseEvent) => {
        if (
          !inputRef.current?.contains(e.target as Node) &&
          !dropdownRef.current?.contains(e.target as Node)
        ) {
          setIsOpen(false);
          setHighlighted(-1);
        }
      };
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // Scroll highlighted item into view
    useEffect(() => {
      if (highlighted >= 0 && dropdownRef.current) {
        const item = dropdownRef.current.children[highlighted] as HTMLElement;
        item?.scrollIntoView({ block: "nearest" });
      }
    }, [highlighted]);

    // Handle input typing
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = e.target.value;
      isTypingRef.current = true;
      setSearchTerm(val);
      onChange(""); // Clear selected employee ID while typing
      setSelectedEmployee(null);
      setIsOpen(true);
      setHighlighted(0);
    };

    const handleSelect = (employee: HRBasicData) => {
      onChange(employee._id);
      setSelectedEmployee(employee);
      setSearchTerm(`${employee.firstName} ${employee.lastName}`);
      setIsOpen(false);
      isTypingRef.current = false;
      cacheRef.current.set(employee._id, employee);
    };

    const handleClear = () => {
      onChange("");
      setSearchTerm("");
      setSelectedEmployee(null);
      setHighlighted(-1);
      setIsOpen(true);
      setTimeout(() => inputRef.current?.focus(), 0);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (onKeyDown) onKeyDown(e);

      if (!isOpen && (e.key === "ArrowDown" || e.key === "Enter")) {
        e.preventDefault();
        setIsOpen(true);
        return;
      }

      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          setHighlighted((i) => Math.min(i + 1, filteredEmployees.length - 1));
          break;
        case "ArrowUp":
          e.preventDefault();
          setHighlighted((i) => Math.max(i - 1, -1));
          break;
        case "Enter":
          e.preventDefault();
          if (highlighted >= 0) handleSelect(filteredEmployees[highlighted]);
          break;
        case "Escape":
          setIsOpen(false);
          setHighlighted(-1);
          break;
      }
    };

    return (
      <div className="w-full relative">
        {label && typeof label === 'string' && (
          <label className="block text-sm font-medium mb-1.5 text-gray-700">
            {label}
            {required && <span className="text-red-500">*</span>}
          </label>
        )}

        <div
          className={`relative flex items-center transition-all duration-200 ${
            inTable
              ? "bg-transparent cursor-text" // Excel-like table cell: no borders, transparent background
              : disabled
              ? "bg-gray-50 cursor-not-allowed border-gray-200 border rounded-lg"
              : isOpen
              ? "bg-white border-blue-500 ring-2 ring-blue-100 cursor-text border rounded-lg"
              : "bg-white border-gray-300 hover:border-gray-400 cursor-text border rounded-lg"
          }`}
          onClick={() => !disabled && inputRef.current?.focus()}
        >
          {!inTable && (
            <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
              <User size={18} />
            </div>
          )}

          <input
            ref={inputRef}
            type="text"
            value={searchTerm}
            onChange={handleInputChange}
            onFocus={() => !disabled && setIsOpen(true)}
            onBlur={() => {
              if (!ignoreBlur.current) setIsOpen(false);
            }}
            onKeyDown={handleKeyDown}
            placeholder={placeholder || t("tableHeaders.employee")}
            className={`w-full bg-transparent focus:outline-none ${
              inTable
                ? 'py-0 px-1 h-6' // Excel-like table cell: minimal padding, fixed height
                : 'py-2.5 pl-10 pr-10' // Regular form input: normal padding with icon space
            } ${disabled ? 'cursor-not-allowed text-gray-500' : ''}`}
            autoComplete="off"
            disabled={disabled}
          />

          {loading && !inTable && (
            <div className="absolute right-10 top-1/2 -translate-y-1/2">
              <SmallLoadingSpinner />
            </div>
          )}

          {(value || searchTerm) && !disabled && !inTable && (
            <button
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={handleClear}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors z-10"
              title="Clear"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        {isOpen && !disabled && (
          <ul
            ref={dropdownRef}
            onMouseDown={() => (ignoreBlur.current = true)}
            onMouseUp={() => (ignoreBlur.current = false)}
            className="absolute z-[9999] mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-xl overflow-y-auto max-h-[200px]"
            role="listbox"
          >
            {loading ? (
              <li className="px-4 py-3 text-gray-500 flex items-center justify-center">
                <Loader2 className="animate-spin h-5 w-5 text-blue-500 mr-2" />
                {t("common.loading")}
              </li>
            ) : filteredEmployees.length === 0 ? (
              value && selectedEmployee ? (
                <li className="px-4 py-2.5 cursor-pointer bg-blue-100 border-l-4 border-blue-500">
                  <div className="font-medium">
                    {selectedEmployee.firstName} {selectedEmployee.lastName}
                  </div>
                </li>
              ) : (
                <li className="px-4 py-3 text-gray-500 bg-gray-50">
                  {t("forms.noResults")}
                </li>
              )
            ) : (
              filteredEmployees.map((employee, index) => (
                <li
                  key={employee._id}
                  className={`px-4 py-2.5 cursor-pointer transition-colors ${
                    highlighted === index ? "bg-blue-50" : "hover:bg-gray-50"
                  } ${value === employee._id ? "bg-blue-100 border-l-4 border-blue-500" : ""}`}
                  onClick={() => handleSelect(employee)}
                  onMouseEnter={() => setHighlighted(index)}
                  role="option"
                  aria-selected={value === employee._id}
                  id={`employee-item-${index}`}
                >
                  <div className="font-medium">
                    {employee.firstName} {employee.lastName}
                  </div>
                </li>
              ))
            )}
          </ul>
        )}
      </div>
    );
  }
);

EmployeeInputSelect.displayName = "EmployeeInputSelect";

export default EmployeeInputSelect;
